import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Fade,
  Stack,
  Typography,
  CircularProgress,
  Portal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Theme,
} from '@mui/material';
import { zIndex } from '@nexus/ari-lib';
import { useWorkspaces, Workspace } from '../hooks/use-workspaces';
import { useTranslation } from 'react-i18next';

type WorkspaceSwitchOverlayProps = {
  /** The workspace being opened. Null closes the overlay. */
  workspace: Workspace | null;
  timeoutDialogOpen: boolean;
  closeTimeoutDialog: () => void;
  switchError: string | null;
  closeSwitchErrorDialog: () => void;
};

//  Covers the app while the target workspace loads.
export function WorkspaceSwitchOverlay({
  workspace,
  timeoutDialogOpen,
  closeTimeoutDialog,
  switchError,
  closeSwitchErrorDialog,
}: WorkspaceSwitchOverlayProps) {
  return (
    <>
      <Portal>
        <Fade in={Boolean(workspace)} unmountOnExit>
          <Box
            data-testid="cloud.workspace.switch-overlay"
            role="status"
            aria-live="polite"
            sx={(theme) => ({
              position: 'fixed',
              inset: 0,
              zIndex: zIndex.tooltip,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // Dims and defocuses the page behind without hiding it.
              bgcolor: alpha(theme.palette.grey[950], 0.6),
              backdropFilter: 'blur(2px)',
            })}
          >
            <Stack spacing={2.5} sx={{ alignItems: 'center', px: 3, maxWidth: 360, textAlign: 'center' }}>
              <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'grey.50' }}>
                  {workspace?.shortName ?? ''}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Opening workspace...
                </Typography>
              </Stack>

              <CircularProgress size={28} />
            </Stack>
          </Box>
        </Fade>
      </Portal>

      <SwitchTimeoutDialog
        testId="switch-timeout-dialog"
        closeTimeoutDialog={closeTimeoutDialog}
        timeoutDialogOpen={timeoutDialogOpen}
      />

      <SwitchErrorDialog
        testId="switch-error-dialog"
        switchError={switchError}
        closeSwitchErrorDialog={closeSwitchErrorDialog}
      />
    </>
  );
}

const cancelButtonSx = (theme: Theme) => ({
  flex: 1,
  borderRadius: 1.5,
  fontWeight: 700,
  textTransform: 'none' as const,
  py: 1.25,
  color: theme.palette.grey[50],
  borderColor: theme.palette.grey[700],
  '&:hover': {
    bgcolor: theme.palette.grey[800],
    borderColor: theme.palette.grey[600],
  },
});

function SwitchErrorDialog({
  switchError,
  closeSwitchErrorDialog,
  testId,
}: {
  switchError: string | null;
  closeSwitchErrorDialog: () => void;
  testId: string;
}) {
  const theme = useTheme();
  const { t: translate } = useTranslation();
  const { refetch } = useWorkspaces();

  const onClose = () => {
    closeSwitchErrorDialog();
    refetch();
  };

  return (
    <Dialog
      open={switchError !== null}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      data-testid={testId}
      slotProps={{
        paper: {
          sx: { bgcolor: 'container.low', borderRadius: 2, textAlign: 'center' },
        },
        backdrop: {
          sx: { bgcolor: 'rgba(0, 0, 0, 0.85)' },
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center' }}>{translate('workspaces.errorTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: theme.palette.grey[400] }}>
          {translate(`workspaces.${switchError}`)}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', gap: 1.5, pb: 3 }}>
        <Button data-testid={`${testId}.close`} variant="outlined" onClick={onClose} sx={cancelButtonSx}>
          {translate('accessibility.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SwitchTimeoutDialog({
  timeoutDialogOpen,
  closeTimeoutDialog,
  testId,
}: {
  timeoutDialogOpen: boolean;
  closeTimeoutDialog: () => void;
  testId: string;
}) {
  const theme = useTheme();
  const { t: translate } = useTranslation();

  return (
    <Dialog
      open={timeoutDialogOpen}
      onClose={closeTimeoutDialog}
      maxWidth="xs"
      fullWidth
      data-testid={testId}
      slotProps={{
        paper: {
          sx: { bgcolor: 'container.low', borderRadius: 2, textAlign: 'center' },
        },
        backdrop: {
          sx: { bgcolor: 'rgba(0, 0, 0, 0.85)' },
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center' }}>{translate('workspaces.timeoutTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: theme.palette.grey[400] }}>
          {translate('workspaces.timeoutMessage')}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', gap: 1.5, pb: 3 }}>
        <Button data-testid={`${testId}.close`} variant="outlined" onClick={closeTimeoutDialog} sx={cancelButtonSx}>
          {translate('accessibility.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
